import {
  getHelpDeskReply,
  processDictation,
  processFullScribe as simulateFullScribe,
  type GhostSuggestion,
  type OrderLane,
  type SoapSection,
} from "./mockAiService";
import { getAgentUrls } from "./agentEndpoint";
import { authHeaders } from "./auth";

export type AgentMode = "live" | "simulation";

export type AgentStatus = {
  mode: AgentMode;
  provider: string;
  model: string;
};

type AgentResponse<T> = {
  mode: AgentMode;
  provider: string;
  model: string;
  traceId?: string;
  result: T;
};

export type OrderDictationSummary = {
  transcript: string;
  summary: string;
  evidence: string;
  mode: AgentMode;
  model: string;
  suggestedLane: OrderLane;
};

const simulationStatus: AgentStatus = {
  mode: "simulation",
  provider: "Safe demo fallback",
  model: "Nong SATI mock clinical agent",
};

async function invokeAgent<T>(task: string, payload: Record<string, unknown>): Promise<AgentResponse<T>> {
  const { invokeUrl } = getAgentUrls();
  const response = await fetch(invokeUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({
      task,
      payload,
      context: {
        patientToken: "demo-ipd-athena",
        activeProblems: ["Anemia", "T2DM", "HT"],
        latestEvidence: ["Hb 7.2 g/dL · CBC 08:00", "Mg 1.6 mg/dL · Chem7 08:00"],
      },
    }),
  });

  if (!response.ok) {
    throw new Error("Nong SATI Agent is unavailable");
  }

  return response.json() as Promise<AgentResponse<T>>;
}

export async function getAgentStatus(): Promise<AgentStatus> {
  try {
    const { statusUrl } = getAgentUrls();
    const response = await fetch(statusUrl, { method: "GET" });
    if (!response.ok) return simulationStatus;
    const status = await response.json() as { configured?: boolean; provider?: string; model?: string };
    if (!status.configured) return simulationStatus;
    return {
      mode: "live",
      provider: status.provider ?? "Sati Agent Gateway",
      model: status.model ?? "Nong SATI · Amazon Bedrock",
    };
  } catch {
    return simulationStatus;
  }
}

export type StructuredEncounter = {
  soap: Record<SoapSection, string>;
  orders: Array<{ category: string; label: string; details: string; lane: "one-day" | "continuous"; stat: boolean }>;
  mode: "live" | "simulation";
};

/**
 * Structure a dictation transcript into a full SOAP note + draft orders via the
 * Bedrock-backed gateway (structure-encounter). Falls back to the simulation if
 * the gateway is unavailable or returns nothing usable.
 */
export async function structureEncounter(transcript: string): Promise<StructuredEncounter> {
  try {
    const response = await invokeAgent<{
      soap?: Record<SoapSection, string>;
      orders?: StructuredEncounter["orders"];
    }>("structure-encounter", { transcript });
    const soap = response.result.soap;
    if (soap && (soap.subjective || soap.objective || soap.assessment || soap.plan)) {
      return {
        soap: {
          subjective: soap.subjective ?? "",
          objective: soap.objective ?? "",
          assessment: soap.assessment ?? "",
          plan: soap.plan ?? "",
        },
        orders: Array.isArray(response.result.orders) ? response.result.orders : [],
        mode: response.mode,
      };
    }
    throw new Error("Empty structuring result");
  } catch {
    const suggestions = await simulateFullScribe();
    const soap: Record<SoapSection, string> = { subjective: "", objective: "", assessment: "", plan: "" };
    for (const suggestion of suggestions) soap[suggestion.field] = suggestion.text;
    return {
      soap,
      orders: [
        { category: "lab", label: "CBC", details: "Tomorrow 08:00", lane: "one-day", stat: false },
        { category: "lab", label: "Stool occult blood", details: "Next specimen", lane: "one-day", stat: false },
      ],
      mode: "simulation",
    };
  }
}

/**
 * One-press Scribe: structure a single recording into all four SOAP fields.
 * Tries the live gateway per field, falling back to the safe simulation for any
 * field the agent can't ground. Returns a suggestion routed to each section.
 */
export async function processFullScribe(): Promise<GhostSuggestion[]> {
  const fields: SoapSection[] = ["subjective", "objective", "assessment", "plan"];
  try {
    const results = await Promise.all(
      fields.map(async (field) => {
        const response = await invokeAgent<GhostSuggestion>("soap-dictation", { field, scribe: true });
        return { ...response.result, field, mode: response.mode };
      }),
    );
    if (results.every((item) => item.text?.trim())) return results;
    throw new Error("Incomplete scribe result");
  } catch {
    return simulateFullScribe();
  }
}

export async function processSoapDictation(field: SoapSection): Promise<GhostSuggestion & AgentStatus> {
  try {
    const response = await invokeAgent<GhostSuggestion>("soap-dictation", { field });
    return {
      ...response.result,
      mode: response.mode,
      provider: response.provider,
      model: response.model,
    };
  } catch {
    const fallback = await processDictation(field);
    return { ...fallback, ...simulationStatus };
  }
}

export async function summarizeOrderDictation(preferredLane?: OrderLane): Promise<OrderDictationSummary> {
  try {
    const response = await invokeAgent<{
      transcript: string;
      summary: string;
      evidence?: string;
      suggestedLane?: OrderLane;
    }>("order-dictation", { preferredLane: preferredLane ?? "auto" });
    return {
      transcript: response.result.transcript,
      summary: response.result.summary,
      evidence: response.result.evidence ?? "Transient dictation audio · clinician confirmation required",
      suggestedLane: response.result.suggestedLane ?? preferredLane ?? "one-day",
      mode: response.mode,
      model: response.model,
    };
  } catch {
    await new Promise((resolve) => setTimeout(resolve, 650));
    return {
      transcript: "Paracetamol five hundred milligrams one tablet three times after meals. CBC tomorrow morning. Normal saline one litre at eighty cc per hour continue.",
      summary: "Paracetamol 500 mg 1×3 PO pc, CBC tomorrow 08:00, NSS 1,000 mL IV 80 cc/hr continue",
      evidence: "Transient order dictation · Thai/English medical ASR simulation",
      suggestedLane: preferredLane ?? "one-day",
      ...simulationStatus,
    };
  }
}

export async function getGroundedHelpDeskReply(input: string) {
  try {
    const response = await invokeAgent<{ text: string; citation: string }>("help-desk", { question: input });
    return {
      ...response.result,
      mode: response.mode,
      model: response.model,
    };
  } catch {
    const fallback = await getHelpDeskReply(input);
    return { ...fallback, ...simulationStatus };
  }
}
